import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import AHBoolean from "_shared/booleans/boolean";
import AHDate from "_shared/dates/date";
import AHDateSchema from "_shared/dates/date-schema";
import Guid from "_shared/guids/guid";
import { z } from "zod";

export type FeeScheduleTemplateFilterJSON = z.infer<
  typeof FeeScheduleTemplateFilterSchema.FeeScheduleTemplateFilterJSONZ
>;
export type FeeScheduleTemplateFilterParams = z.infer<
  typeof FeeScheduleTemplateFilterSchema.FeeScheduleTemplateFilterZ
>;

export default class FeeScheduleTemplateFilterSchema {
  public static readonly FeeScheduleTemplateFilterJSONZ = z.object({
    query: z.string().nullish(),
    entityId: z.uuid(),
    creatorUserId: z.uuid().nullish(),
    name: z.string().nullish(),
    minCreatedDate: z.iso.datetime({ offset: true }).nullish(),
    maxCreatedDate: z.iso.datetime({ offset: true }).nullish(),
    tags: z.array(z.string()).nullish(),
    isArchived: z.boolean().nullish(),
    isActive: z.boolean().nullish(),
  });
  public static readonly FeeScheduleTemplateFilterZ = z.object({
    query: z.string().optional(),
    entityId: z.instanceof(Guid),
    creatorUserId: z.instanceof(Guid).optional(),
    name: z.string().optional(),
    minCreatedDate: AHDateSchema.type.optional(),
    maxCreatedDate: AHDateSchema.type.optional(),
    tags: z.array(z.string()).optional(),
    isArchived: AHBoolean.type.optional(),
    isActive: AHBoolean.type.optional(),
  });

  public static validate(values: unknown): FeeScheduleTemplateFilterParams {
    return parseOrThrow(FeeScheduleTemplateFilterSchema.FeeScheduleTemplateFilterZ, values, {
      logError: true,
      schemaName: "FeeScheduleTemplateQuerySchema",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validateJSON(values: unknown): FeeScheduleTemplateFilterJSON {
    return parseOrThrow(FeeScheduleTemplateFilterSchema.FeeScheduleTemplateFilterJSONZ, values, {
      logError: true,
      schemaName: "FeeScheduleTemplateQuerySchema",
      operation: "validateJSON",
      payloadSample: values,
    });
  }

  public static isValid(values: unknown): values is FeeScheduleTemplateFilterParams {
    try {
      FeeScheduleTemplateFilterSchema.validate(values);
      return true;
    } catch {
      return false;
    }
  }

  public static isValidJSON(values: unknown): values is FeeScheduleTemplateFilterJSON {
    try {
      FeeScheduleTemplateFilterSchema.validateJSON(values);
      return true;
    } catch {
      return false;
    }
  }

  public static deserialize(data: FeeScheduleTemplateFilterJSON): FeeScheduleTemplateFilterParams {
    const parsed = parseOrThrow(FeeScheduleTemplateFilterSchema.FeeScheduleTemplateFilterJSONZ, data, {
      logError: true,
      schemaName: "FeeScheduleTemplateQuerySchema",
      operation: "deserialize",
      payloadSample: data,
    });
    return FeeScheduleTemplateFilterSchema.codec.decode(parsed);
  }

  public static serialize(filter: FeeScheduleTemplateFilterParams): FeeScheduleTemplateFilterJSON {
    const parsed = FeeScheduleTemplateFilterSchema.validate(filter);
    return FeeScheduleTemplateFilterSchema.codec.encode(parsed);
  }

  private static readonly codec = z.codec(
    FeeScheduleTemplateFilterSchema.FeeScheduleTemplateFilterJSONZ,
    FeeScheduleTemplateFilterSchema.FeeScheduleTemplateFilterZ,
    {
      decode: (filterJSON) => ({
        query: filterJSON.query ?? undefined,
        entityId: Guid.fromJSON(filterJSON.entityId)!,
        creatorUserId: Guid.fromJSON(filterJSON.creatorUserId) ?? undefined,
        name: filterJSON.name ?? undefined,
        minCreatedDate: AHDate.fromJSON(filterJSON.minCreatedDate) ?? undefined,
        maxCreatedDate: AHDate.fromJSON(filterJSON.maxCreatedDate) ?? undefined,
        tags: filterJSON.tags ?? undefined,
        isArchived: AHBoolean.fromJSON(filterJSON.isArchived) ?? undefined,
        isActive: AHBoolean.fromJSON(filterJSON.isActive) ?? undefined,
      }),
      encode: (filterParams) => ({
        query: filterParams.query ?? null,
        entityId: filterParams.entityId.toJSON(),
        creatorUserId: filterParams.creatorUserId?.toJSON(),
        name: filterParams.name ?? null,
        minCreatedDate: filterParams.minCreatedDate?.toJSON(),
        maxCreatedDate: filterParams.maxCreatedDate?.toJSON(),
        tags: filterParams.tags ?? null,
        isArchived: filterParams.isArchived?.toJSON() ?? null,
        isActive: filterParams.isActive?.toJSON() ?? null,
      }),
    }
  );
}
